import {
  Alert,
  Button,
  Card,
  Heading,
  Paragraph,
  Skeleton,
} from "@digdir/designsystemet-react";
import { ChevronRightIcon, PlusIcon } from "@navikt/aksel-icons";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { httpClient } from "@/api/client";
import { getAvtalerOptions } from "@/api/generated/@tanstack/react-query.gen";
import { NyttigeLenker, Page } from "@/components";
import { Avtale } from "./components/Avtale";
import { AvtaleHeader } from "./components/AvtaleHeader";

export const AvtalerPage = () => {
  const { t } = useTranslation();
  const router = useRouter();

  const {
    data: avtalerOgBestillinger,
    isLoading: isLoadingAvtaler,
    isError: isErrorAvtaler,
  } = useQuery({
    ...getAvtalerOptions({
      client: httpClient,
    }),
  });

  // A negative value indicates that a should come before b.
  // A positive value indicates that a should come after b.
  // Zero or NaN indicates that a and b are considered equal.
  const sortedAvtaler = avtalerOgBestillinger?.avtaler.slice().sort((a, b) => {
    // "Aktiv" avtaler should be sorted first
    if (a.avtaleStatus === "Aktiv" && b.avtaleStatus !== "Aktiv") {
      return -1;
    }
    if (a.avtaleStatus !== "Aktiv" && b.avtaleStatus === "Aktiv") {
      return 1;
    }

    // Sort by most recent created first
    var createdAtA = new Date(a.createdAt);
    var createdAtB = new Date(b.createdAt);
    var sortByCreated = createdAtB.getTime() - createdAtA.getTime();
    if (Number.isNaN(sortByCreated)) {
      // If createdAt is invalid date, keep original order
      return 0;
    }
    return sortByCreated;
  });

  const hasNoAvtaler =
    sortedAvtaler === undefined || sortedAvtaler.length === 0;

  return (
    <>
      <AvtaleHeader />
      <Page className="flex flex-col xl:flex-row flex-wrap gap-6">
        <section className="flex flex-col gap-6 flex-1 max-w-2xl">
          <Heading level={1} data-size="md">
            {t("pages.avtaler.avtaler.tittel")}
          </Heading>
          <Paragraph>{t("pages.avtaler.avtaler.beskrivelse")}</Paragraph>

          {isLoadingAvtaler && (
            <div className="space-y-5">
              <Skeleton variant="circle" className="w-56 h-12" />

              <Skeleton variant="rectangle" className="w-full h-50" />
            </div>
          )}
          {isErrorAvtaler && (
            <Alert data-color="danger">
              <Heading data-size="xs">{t("skjema.feil-ved-henting")}</Heading>
            </Alert>
          )}

          {hasNoAvtaler && (
            <Card
              data-size="md"
              variant="tinted"
              className="w-full border-dashed flex flex-row justify-between items-center"
            >
              <span>{t("pages.avtaler.avtaler.ingen-aktive-avtaler")}</span>
              <Button
                variant="primary"
                className="w-fit"
                onClick={() => router.navigate({ to: "/bestilling/ny" })}
              >
                {t("pages.avtaler.avtaler.bestill-norgespris")}
                <PlusIcon fontSize="1.5rem" className="shrink-0" />
              </Button>
            </Card>
          )}
          {!hasNoAvtaler && (
            <Button
              variant="primary"
              className="w-fit"
              onClick={() => router.navigate({ to: "/bestilling/ny" })}
            >
              {t("pages.avtaler.avtaler.legg-til-ny-bestillinger")}
              <ChevronRightIcon fontSize="1.5rem" className="shrink-0" />
            </Button>
          )}

          {sortedAvtaler && (
            <div className="flex flex-col gap-4 w-full">
              {sortedAvtaler?.map((avtale) => (
                <Avtale key={avtale.avtaleId} avtale={avtale} />
              ))}
            </div>
          )}
        </section>
        <NyttigeLenker className="ml-auto" />
      </Page>
    </>
  );
};
